import { Component, OnInit, ChangeDetectorRef, SecurityContext, inject, signal, viewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ConnectorLocationResponse,
  ConnectorResponse,
  ConnectorSearchEvent,
  PriceType,
} from 'src/app/features/charging-station-location/charging-station-location-model';
import { ConnectorService } from '../../services/connector.service';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { DkCurrencyPipe } from '../../pipes/dk-currency';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'adm-connector-selector',
  templateUrl: './connector-selector.component.html',
  styleUrls: ['./connector-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    MatTooltipModule,
    LoadingSpinnerComponent,
    DkCurrencyPipe,
  ],
})
export class ConnectorSelectorComponent implements OnInit {
  protected changeDetectorRef = inject(ChangeDetectorRef);
  dialogRef = inject<MatDialogRef<ConnectorSelectorComponent>>(MatDialogRef);
  data = inject<{
    organizationId: number;
    selectedConnectorIdentifiers: string[];
    chargingStationIdentifier: string;
    fromTerminalConnectorRelationModal: boolean;
    terminalSerialNumber: string;
  }>(MAT_DIALOG_DATA);
  private connectorService = inject(ConnectorService);
  private sanitizer = inject(DomSanitizer);

  public connectorLocationItems: ConnectorLocationResponse[] = [];
  public allConnectorLocationItems: ConnectorLocationResponse[] = [];
  public filteredConnectorLocationItems: ConnectorLocationResponse[] = [];
  public selectedConnectors: ConnectorResponse[] = [];
  public selectedConnectorIdentifiers: string[] = [];
  public chargingStationIdentifier = '';
  connectors = viewChild<any>('connectors');
  connectorCheckboxes = new FormControl();
  public filterModel: FormGroup = this.initForm();
  public loading = signal<boolean>(false);
  public connectorSearchEvent: ConnectorSearchEvent = {} as ConnectorSearchEvent;
  public toggleSelectedConnectors = false;
  pricetypes: typeof PriceType = PriceType;

  get query() {
    return this.filterModel.controls['query'];
  }
  get selectAllLocations() {
    return this.filterModel.controls['selectAllLocations'];
  }

  ngOnInit(): void {
    this.loading.set(true);

    this.selectedConnectorIdentifiers = this.data?.selectedConnectorIdentifiers;
    this.connectorSearchEvent.organizationId = this.data.organizationId;
    this.chargingStationIdentifier = this.data.chargingStationIdentifier;

    this.connectorService.getConnectorsOnOrganization(this.connectorSearchEvent, this.data.terminalSerialNumber).subscribe({
      next: conns => {
        this.connectorLocationItems = conns;
        this.allConnectorLocationItems = conns;
        this.filteredConnectorLocationItems = conns;
        this.initConnectorLocations();

        if (this.data.fromTerminalConnectorRelationModal) {
          this.loadselectedConnectors();
        }

        this.loading.set(false);
        //due to ExpressionChangedAfterItHasBeenCheckedError when populating matBadge counter
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.loading.set(false);
      },
    });
    //due to ExpressionChangedAfterItHasBeenCheckedError when populating matBadge counter
    this.changeDetectorRef.detectChanges();

    // NOTE: The backend supports filtering too. In case the list grows large, consider if filtering in backend is more useful.
    this.filterModel.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(val => {
        let query = val?.query as string;
        if (query) {
          query = query.toLowerCase().trim();

          this.filteredConnectorLocationItems = this.allConnectorLocationItems.filter(
            c =>
              c.connectors.length > 0 &&
              (c.name.toLowerCase().includes(query) ||
                c.address.toLowerCase().includes(query) ||
                c.departmentName.toLowerCase().includes(query) ||
                c.connectors.some(co => co.chargingStationIdentifier.toLowerCase().includes(query)))
          );
        } else {
          this.filteredConnectorLocationItems = this.allConnectorLocationItems;
        }

        this.filteredConnectorLocationItems
          .flatMap(f => f.connectors)
          .forEach(connectorItem => {
            connectorItem.isSelected =
              this.selectedConnectors.findIndex(c => c.connectorIdentifier === connectorItem.connectorIdentifier) > -1;
          });
      });

    this.selectAllLocations.valueChanges.subscribe(selectAll => {
      this.filteredConnectorLocationItems.forEach(connectorLocation => {
        connectorLocation.connectors.forEach(connector => {
          connector.isSelected = selectAll;
        });
        connectorLocation.selectedCount = connectorLocation.connectors.filter(c => c.isSelected).length;
        if (connectorLocation.connectors.length > 0) {
          connectorLocation.allSelected = connectorLocation.connectors.length === connectorLocation.selectedCount;
        }
      });
      this.updateSelectedConnectors();
    });
  }

  public resetConnectors() {
    this.selectAllLocations.setValue(false);
    this.connectorLocationItems.forEach(connectorLocation => {
      connectorLocation.connectors.forEach(connector => {
        connector.isSelected = false;
      });
      connectorLocation.selectedCount = 0;
      connectorLocation.allSelected = false;
    });
    this.updateSelectedConnectors();
  }

  public loadselectedConnectors() {
    this.toggleSelectedConnectors = !this.toggleSelectedConnectors;
    if (this.toggleSelectedConnectors) {
      const selectedConnectorIdentifers = this.connectorLocationItems
        .flatMap(c => c.connectors)
        .filter(c => c.isSelected)
        .map(c => c.connectorIdentifier);

      this.filteredConnectorLocationItems = this.connectorLocationItems.filter(c =>
        c.connectors.some(co => selectedConnectorIdentifers.includes(co.connectorIdentifier))
      );
    } else {
      this.filteredConnectorLocationItems = this.allConnectorLocationItems;
    }
  }

  updateParent(connectorLocation: ConnectorLocationResponse, connectorResponse: ConnectorResponse) {
    const selectableConnectors = connectorLocation.connectors.filter(c => !c.isAttachedToDifferentTerminal);
    connectorResponse.isSelected = !connectorResponse.isSelected;
    connectorLocation.selectedCount = selectableConnectors.filter(c => c.isSelected).length;
    connectorLocation.allSelected = selectableConnectors.length > 0 && selectableConnectors.length === connectorLocation.selectedCount;

    this.updateSelectedConnectors();
  }

  initConnectorLocations() {
    this.selectAllLocations.setValue(false);
    this.connectorLocationItems.forEach(connectorLocation => {
      connectorLocation.connectors.forEach(connector => {
        connector.isSelected =
          this.selectedConnectorIdentifiers?.includes(connector.connectorIdentifier) ||
          connector.chargingStationIdentifier === this.chargingStationIdentifier;
      });
      const selectableConnectors = connectorLocation.connectors.filter(c => !c.isAttachedToDifferentTerminal);
      connectorLocation.selectedCount = connectorLocation.connectors.filter(c => c.isSelected).length;
      connectorLocation.allSelected =
        selectableConnectors.length > 0 && selectableConnectors.length === selectableConnectors.filter(c => c.isSelected).length;
    });
    this.updateSelectedConnectors();
  }

  someSelected(connectorLocation: ConnectorLocationResponse): boolean {
    return (
      connectorLocation.selectedCount > 0 &&
      connectorLocation.selectedCount < connectorLocation.connectors.filter(c => !c.isAttachedToDifferentTerminal).length
    );
  }

  hasAvailableConnectors(connectorLocation: ConnectorLocationResponse): boolean {
    return connectorLocation.connectors.length > 0 && connectorLocation.connectors.some(c => !c.isAttachedToDifferentTerminal);
  }

  selectAll(connectorLocation: ConnectorLocationResponse) {
    const selectableConnectors = connectorLocation.connectors.filter(c => !c.isAttachedToDifferentTerminal);

    selectableConnectors.forEach(connector => {
      connector.isSelected = !connectorLocation.allSelected;
    });

    connectorLocation.selectedCount = selectableConnectors.filter(c => c.isSelected).length;
    connectorLocation.allSelected = selectableConnectors.length === connectorLocation.selectedCount;

    this.updateSelectedConnectors();
  }

  updateSelectedConnectors() {
    this.selectedConnectors = this.connectorLocationItems.flatMap(c => c.connectors.filter(c => c.isSelected));

    const selectedIdentifiers = this.selectedConnectors.map(c => c.connectorIdentifier);
    this.selectedConnectors = [
      ...this.selectedConnectors,
      ...this.filteredConnectorLocationItems.flatMap(c =>
        c.connectors.filter(c => c.isSelected && !selectedIdentifiers.includes(c.connectorIdentifier))
      ),
    ];
  }
  saveAndClose() {
    this.selectedConnectorIdentifiers = this.selectedConnectors.map(c => c.connectorIdentifier);
    this.dialogRef.close(this.selectedConnectorIdentifiers);
  }

  close() {
    this.dialogRef.close(false);
  }

  private initForm() {
    return new FormGroup({
      query: new FormControl(''),
      selectAllLocations: new FormControl({ value: false, disabled: this.allConnectorLocationItems?.length === 0 }),
    });
  }
}
